import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Person, PersonObservation } from '../api/types.js';
import { useIdentityDecisions, usePeople, usePersonIdentity } from '../hooks/use-people.js';
import { useServers } from '../hooks/use-servers.js';
import { Button } from './button.js';

// A person across servers (ADR-030 section 8): the same person seen by several clients, tied together by the operator.
// Each server keeps what it learnt; this puts them side by side for the operator only, and no session ever sees it.

const KIND: Record<PersonObservation['kind'], string> = { expectation: 'Expects', recurring: 'Comes back to', practice: 'Works this way', 'review-point': 'Reviews point out' };
const select = 'min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm';
const selectStyle = { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' };

function repoLine(activity: Record<string, { prs: number; merged: number; tickets: number; comments: number }>): string {
  return Object.entries(activity)
    .sort((a, b) => (b[1].prs + b[1].tickets + b[1].comments) - (a[1].prs + a[1].tickets + a[1].comments))
    .slice(0, 4)
    .map(([repo, f]) => `${repo}: ${[f.prs && `${f.prs} PRs`, f.tickets && `${f.tickets} tickets`, f.comments && `${f.comments} comments`].filter(Boolean).join(', ')}`)
    .join('; ');
}

/** Link by hand: a server, then one of its people */
function LinkByHand({ serverId, person, onLink, busy }: { serverId: string; person: Person; onLink: (o: { serverId: string; personId: string }) => void; busy: boolean }) {
  const { data: servers } = useServers();
  const [server, setServer] = useState('');
  const [target, setTarget] = useState('');
  const { data: theirs, isLoading } = usePeople(server, !!server);
  const others = (servers ?? []).filter((s) => s.id !== serverId);
  if (!others.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <select value={server} onChange={(e) => { setServer(e.target.value); setTarget(''); }} aria-label={`A server where ${person.name} also works`} className={select} style={selectStyle}>
        <option value="">Link to someone on another server…</option>
        {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {server && (
        <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={isLoading} aria-label="The same person on that server" className={select} style={selectStyle}>
          <option value="">{isLoading ? 'Loading its people…' : 'Pick the person'}</option>
          {(theirs?.people ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.facets.length ? ` (${p.facets.map((f) => f.login).join(', ')})` : ''}</option>)}
        </select>
      )}
      {server && (
        <Button size="sm" disabled={!target || busy} onClick={() => onLink({ serverId: server, personId: target })}>
          {busy ? 'Linking…' : 'Link'}
        </Button>
      )}
    </div>
  );
}

export function OtherServers({ serverId, person }: { serverId: string; person: Person }) {
  const { data, error } = usePersonIdentity(serverId, person.id, true);
  const { link, unlink } = useIdentityDecisions(serverId, person.id);
  const failure = link.error ?? unlink.error;
  return (
    <div>
      <h3 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>On other servers</h3>
      {error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not loaded: {(error as Error).message}</p>}
      {!data && !error && <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Looking at the other servers…</p>}
      {data && (
        <>
          {data.others.length > 0 && (
            <ul className="space-y-3">
              {data.others.map((o) => (
                <li key={`${o.serverId}/${o.personId}`} className="border-l-2 pl-3" style={{ borderColor: 'var(--color-border-2)' }}>
                  <p className="text-sm">
                    <Link to={`/servers/${o.serverId}/people`} className="font-medium underline-offset-2 hover:underline" style={{ color: 'var(--color-text)' }}>{o.serverName}</Link>
                    <span style={{ color: 'var(--color-text-2)' }}>{`, as ${o.name}`}{o.role ? `, ${o.role}` : ''}</span>
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>{repoLine(o.activity) || 'No activity seen there yet'}</p>
                  {o.observations.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {o.observations.map((x) => (
                        <li key={x.id} className="text-xs" style={{ color: 'var(--color-text-2)' }}>
                          <span style={{ color: 'var(--color-text-3)' }}>{KIND[x.kind]}: </span>{x.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          {data.proposals.map((p) => (
            <div key={`${p.serverId}/${p.personId}`} className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--color-text-2)' }}>
              <span>{`The same GitHub account, ${p.account}, is ${p.name} on ${p.serverName}.`}</span>
              <Button size="sm" disabled={link.isPending} onClick={() => link.mutate({ serverId: p.serverId, personId: p.personId })}
                aria-label={`Link ${person.name} to ${p.name} on ${p.serverName}`}>
                Link
              </Button>
            </div>
          ))}
          {data.others.length === 0 && data.proposals.length === 0 && (
            <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Not linked to anyone on your other servers.</p>
          )}
          <LinkByHand serverId={serverId} person={person} busy={link.isPending} onLink={(o) => link.mutate(o)} />
          {data.identity && (
            <Button size="sm" variant="ghost" className="mt-2" disabled={unlink.isPending} onClick={() => unlink.mutate()}>
              {unlink.isPending ? 'Unlinking…' : 'Unlink from the other servers'}
            </Button>
          )}
          {failure && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not saved: {(failure as Error).message}</p>}
          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Each server keeps what it learnt. Sessions never see the other servers.</p>
        </>
      )}
    </div>
  );
}
